/**
 * OneSignal helper — initializes push notifications inside the Capacitor
 * native app (iOS / Android). All methods are safe no-ops when running in a
 * regular web browser, where the Cordova plugin and `cordova` global are not
 * available.
 *
 * Setup at build time:
 *   - Set REACT_APP_ONESIGNAL_APP_ID in the build environment.
 *   - On the OneSignal dashboard, configure the matching iOS APNs key + the
 *     Android Firebase service account JSON.
 */

const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;

// True only inside the Capacitor WebView, where window.cordova is injected.
const isNativeApp = () =>
  typeof window !== "undefined" &&
  (window.cordova !== undefined || window.Capacitor?.isNativePlatform?.());

let initialized = false;

const safeRequire = () => {
  try {
    // eslint-disable-next-line global-require
    return require("onesignal-cordova-plugin");
  } catch (e) {
    return null;
  }
};

export function initOneSignal() {
  if (initialized || !isNativeApp() || !APP_ID) return;
  const OneSignal = safeRequire();
  if (!OneSignal) return;
  try {
    OneSignal.initialize(APP_ID);
    OneSignal.Notifications.addEventListener("click", (event) => {
      const data = event?.notification?.additionalData;
      const requestId = data?.request_id;
      if (requestId && window?.location) {
        window.location.hash = `#/dashboard?request=${requestId}`;
      }
    });
    initialized = true;
  } catch (e) {
    console.warn("OneSignal init failed:", e);
  }
}

export async function requestPushPermission() {
  if (!isNativeApp()) return false;
  const OneSignal = safeRequire();
  if (!OneSignal) return false;
  try {
    return await OneSignal.Notifications.requestPermission(true);
  } catch (e) {
    console.warn("Push permission request failed:", e);
    return false;
  }
}

export function loginPushUser(userId, tags = {}) {
  if (!isNativeApp() || !userId) return;
  const OneSignal = safeRequire();
  if (!OneSignal) return;
  try {
    OneSignal.login(String(userId));
    if (tags && Object.keys(tags).length) {
      OneSignal.User.addTags(tags);
    }
  } catch (e) {
    console.warn("OneSignal login failed:", e);
  }
}

export function logoutPushUser() {
  if (!isNativeApp()) return;
  const OneSignal = safeRequire();
  if (!OneSignal) return;
  try {
    OneSignal.logout();
  } catch (e) {
    console.warn("OneSignal logout failed:", e);
  }
}
